import { createAction } from '@reduxjs/toolkit';
import { ICalendarItemData } from 'Calendar/types';
import { IApiStaffByAppointmentParams, IApiStaffByAppointmentResData, IAppointmentSummaryCallerIdParams, ICalendarDropData, IStateCalendar } from 'services/types/apt';
import { IBlockHourResItem, IBodyAddBlockHours } from 'services/types/blockHours';
import { IApiHistoryCallerIdParams, ICallerIdHistoryResData, IPendingCallerResItem, ICustomerRecentApptsResData, ICustomerApptsPageParams, ICustomerApptsPageResData } from 'services/types/caller';
import { IBodyApiQuickBooking, IQuickBookingServiceItem } from 'services/types/quickBooking';
import { IAppointmentListParams, IPayloadBooking, IPayloadBookingV2 } from 'services/types/request';
import { IAptListResPayload } from 'services/types/response';
import { createAsyncAction } from 'store/actionConfigs';

const PREFIX_ACTIONS = 'appointment_feature_';

const calendar = {
  setDistanceTimeline: createAction<IStateCalendar['distanceTimeLine']>(PREFIX_ACTIONS + '_configs_' + 'setDistanceTimeline'),
  setTypeView: createAction<IStateCalendar['typeView']>(PREFIX_ACTIONS + '_calendar_' + 'setTypeView'),
  setDateRanges: createAction<IStateCalendar['dateRanges']>(PREFIX_ACTIONS + '_calendar_' + 'setDateRanges'),
  updateDrop: createAsyncAction<ICalendarDropData>(PREFIX_ACTIONS + '_calendar_' + 'updateDrop'),
  getData: createAsyncAction<{ shopId?: string, startTime: string }>(PREFIX_ACTIONS + '_calendar_' + 'getData'),
  prolongedTime: createAction<{ id: string, status: 'ADD' | 'MINUS', pinCode: string }>(PREFIX_ACTIONS + '_calendar_' + 'prolongedTime'),
  refresh: createAction(PREFIX_ACTIONS + '_calendar_' + 'refresh'),
  setBlockHoursData: createAction<ICalendarItemData[]>(PREFIX_ACTIONS + 'setBlockHoursData'),
};

const table = {
  getData: createAsyncAction<IAppointmentListParams, IAptListResPayload>(PREFIX_ACTIONS + '_table_' + 'getData'),
  blockHours: createAsyncAction<IAppointmentListParams, IBlockHourResItem[]>(PREFIX_ACTIONS + '_table_' + 'getBlockHours'),
  setParams: createAction<IAppointmentListParams>(PREFIX_ACTIONS + '_table_' + 'setParams'),
};

const customer = {
  getData: createAsyncAction<IAppointmentListParams, IAptListResPayload>(PREFIX_ACTIONS + '_customer_' + 'getData'),
  setParams: createAction<IAppointmentListParams>(PREFIX_ACTIONS + '_customer_' + 'setParams'),
  initData: createAction<IAppointmentListParams>(PREFIX_ACTIONS + '_customer_' + 'initData'),
  clear: createAction(PREFIX_ACTIONS + '_customer_' + 'clear'),
};


const initData = createAction<string>(PREFIX_ACTIONS + 'initData');

const bookApt = createAction<IPayloadBooking>(PREFIX_ACTIONS + 'bookApt');

const bookAptV2 = createAction<IPayloadBookingV2>(PREFIX_ACTIONS + 'bookAptV2');

const deleteApt = createAction<{ aptId: string, pinCode: string }>(PREFIX_ACTIONS + 'deleteApt');

const cancelApt = createAction<{ aptId: string, reason: string, pinCode: string }>(PREFIX_ACTIONS + 'cancelApt');

const confirmApt = createAction<{ appointmentId: string, pinCode: string }>(PREFIX_ACTIONS + 'confirmApt');

const checkInApt = createAction<string>(PREFIX_ACTIONS + 'checkInApt');

const updateBlockHour = createAction<IBodyAddBlockHours>(PREFIX_ACTIONS + 'updateBlockHour');

const addBlockHour = createAction<IBodyAddBlockHours>(PREFIX_ACTIONS + 'addBlockHour');

const deleteBlockHour = createAction<string>(PREFIX_ACTIONS + 'deleteBlockHour');
const onFormDeleteBlockHourSuccess = createAction<string>(PREFIX_ACTIONS + 'onFormDeleteBlockHourSuccess');

const prolongedTimeBlockHour = createAction<{ id: string, status: 'ADD' | 'MINUS' }>(PREFIX_ACTIONS + 'prolongedTimeBlockHour');

const updateApt = createAction<IPayloadBooking>(PREFIX_ACTIONS + 'updateApt');

const updateAptV2 = createAction<IPayloadBookingV2>(PREFIX_ACTIONS + 'updateAptV2');

const quickBooking = {
  getServices: createAsyncAction<string, IQuickBookingServiceItem[]>(PREFIX_ACTIONS + 'Quick-Booking' + 'getServices'),
  doQuickBooking: createAction<IBodyApiQuickBooking[]>(PREFIX_ACTIONS + 'doQuickBooking'),
};

const staffAvailableBooking = createAsyncAction<{ startTime: string, endTime: string }>(PREFIX_ACTIONS + 'getStaffAvailableBooking');

const getStaffByAppointment = createAsyncAction<Partial<IApiStaffByAppointmentParams>, IApiStaffByAppointmentResData>(PREFIX_ACTIONS + 'getStaffByAppointment');

const getCallerIdHistory = createAsyncAction<IApiHistoryCallerIdParams, ICallerIdHistoryResData>(PREFIX_ACTIONS + 'getCallerIdHistory');
const getAppointmentSummaryCallerId = createAsyncAction<IAppointmentSummaryCallerIdParams>(PREFIX_ACTIONS + 'getAppointmentSummaryCallerId');

// Caller V2 slices. `closeIfEmpty` (from a book/SMS action) closes the panel when
// the refreshed pending list comes back empty.
const getPendingCallers = createAsyncAction<{ closeIfEmpty?: boolean } | undefined, IPendingCallerResItem[]>(PREFIX_ACTIONS + 'getPendingCallers');
const getCustomerRecent = createAsyncAction<number, ICustomerRecentApptsResData>(PREFIX_ACTIONS + 'getCustomerRecent');
const getCustomerAppointments = createAsyncAction<ICustomerApptsPageParams, ICustomerApptsPageResData>(PREFIX_ACTIONS + 'getCustomerAppointments');

const blockHourList = createAsyncAction<undefined, IBlockHourResItem[]>(PREFIX_ACTIONS + 'blockHourList');
const getShopSurveyInfo = createAsyncAction(PREFIX_ACTIONS + 'getShopSurveyInfo');
const getCustomerTransactions = createAsyncAction<number>(PREFIX_ACTIONS + 'getCustomerTransactions');

const aptActions = {
  initData,
  calendar,
  table,
  customer,
  bookApt,
  bookAptV2,
  deleteApt,
  updateApt,
  updateAptV2,
  addBlockHour,
  deleteBlockHour,
  updateBlockHour,
  prolongedTimeBlockHour,
  quickBooking,
  cancelApt,
  confirmApt,
  staffAvailableBooking,
  checkInApt,
  onFormDeleteBlockHourSuccess,
  getStaffByAppointment,
  getCallerIdHistory,
  getAppointmentSummaryCallerId,
  getPendingCallers,
  getCustomerRecent,
  getCustomerAppointments,
  blockHourList,
  getShopSurveyInfo,
  getCustomerTransactions
};

export default aptActions;

